import { z } from "zod";

export const workspaceInviteSchema = z.object({
  workspace_ids: z
    .array(
      z.number({
        invalid_type_error: "Please select a valid workspace",
      }),
    )
    .min(1, "At least one workspace is required"),
  emails: z
    .array(z.string())
    .min(1, "At least one email is required")
    .max(50, "Cannot invite more than 50 people at once")
    .superRefine((emails, ctx) => {
      const emptyEmails: string[] = [];
      const invalidEmails: string[] = [];
      const seenLower = new Set<string>();
      let hasDuplicate = false;

      emails.forEach((email, index) => {
        if (!email || email.trim() === "") {
          emptyEmails.push(`Email ${index + 1}`);
        } else if (!z.string().email().safeParse(email).success) {
          invalidEmails.push(email);
        } else {
          const key = email.trim().toLowerCase();
          if (seenLower.has(key)) {
            hasDuplicate = true;
          }
          seenLower.add(key);
        }
      });

      if (hasDuplicate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Each email address must be unique. Remove duplicate addresses.",
        });
      }

      if (emptyEmails.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${emptyEmails.join(", ")} cannot be empty`,
        });
      }

      if (invalidEmails.length > 0) {
        const emailList = invalidEmails.map(email => `"${email}"`).join(", ");
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${emailList} ${
            invalidEmails.length === 1
              ? "is not a valid email address"
              : "are not valid email addresses"
          }`,
        });
      }
    }),
});

export type WorkspaceInviteFormValues = z.infer<typeof workspaceInviteSchema>;
